import React from 'react'
import { Badge, Col, Row } from 'reactstrap'
import { StyledExportButton } from './styles'
import FieldsetItem from 'components/FieldsetItem'
import FieldsetItemLarge from 'components/FieldsetItemLarge'

interface ProductUser {
  id: number
  firstName?: string
  lastName?: string
  email?: string
  contactPhoneNumber?: string
  mobilePhoneNumber?: string
  siteId?: number
  siteName?: string
  title?: string
  roles: string[]
  isSSO?: boolean
  lastLoggedInAt: string
}

interface UserProfileProps {
  displayHSCompletionReportButton?: boolean
  displayK8CompletionReportButton?: boolean
  getLessonProgressReport: (programCode: string) => void
  handleClearUserCache: () => void
  productUser: ProductUser
}

interface TagProps {
  color: string
  text: string
}

const Tag = ({ color, text }: TagProps) => (
  <Badge color={color} key={text} style={{ fontSize: 'initial' }}>
    {text}
  </Badge>
)

export const UserProfile = ({
  displayHSCompletionReportButton,
  displayK8CompletionReportButton,
  getLessonProgressReport,
  handleClearUserCache,
  productUser,
}: UserProfileProps) => {
  const lastLoggedInAtWithOffset = productUser.lastLoggedInAt + '-00:00'
  const lastLoggedInAtFormatted = new Date(
    lastLoggedInAtWithOffset,
  ).toLocaleString('en-US', {
    timeZoneName: 'short',
  })
  const lastLoggedIn =
    lastLoggedInAtFormatted !== 'Invalid Date' ? lastLoggedInAtFormatted : ''

  return (
    <React.Fragment>
      <Col sm={8}>
        <fieldset>
          <legend>Profile</legend>
          <Row>
            <Col>
              <Row>
                <Col>
                  <FieldsetItem
                    data={productUser.firstName || '-'}
                    title="First Name"
                  />
                </Col>
                <Col>
                  <FieldsetItem
                    data={productUser.lastName || '-'}
                    title="Last Name"
                  />
                </Col>
                <Col>
                  <FieldsetItem data={productUser.email || '-'} title="Email" />
                </Col>
              </Row>
              <FieldsetItem
                data={productUser.contactPhoneNumber || '-'}
                title="Contact Number"
              />
              <FieldsetItem
                data={productUser.mobilePhoneNumber || '-'}
                title="Mobile Phone"
              />
              <StyledExportButton
                color="primary"
                onClick={handleClearUserCache}
              >
                Clear User's Cache
              </StyledExportButton>
            </Col>
            <Col>
              <FieldsetItem data={productUser.siteId} title="Site Id" />
              <FieldsetItemLarge
                data={productUser.siteName}
                title="School Name"
              />
              <FieldsetItem data={productUser.title || '-'} title="Job Title" />
            </Col>
          </Row>
        </fieldset>
      </Col>
      <Col>
        <fieldset>
          <legend>Other</legend>
          <FieldsetItem title="Is Setup Admin">
            {productUser.roles.includes('Set-Up Admin') ? (
              <Tag color="info" text="Yes" />
            ) : (
              <Tag color="light" text="No" />
            )}
          </FieldsetItem>
          <FieldsetItem title="Is SSO User">
            {productUser.isSSO ? (
              <Tag color="warning" text="Yes" />
            ) : (
              <Tag color="light" text="No" />
            )}
          </FieldsetItem>
          <FieldsetItem data={lastLoggedIn} title="Last Login" />
          {displayK8CompletionReportButton && (
            <StyledExportButton
              color="primary"
              data-testid="k8-report-export-button"
              onClick={() => getLessonProgressReport('K8')}
              role="button"
              size="sm"
            >
              Export K8 Lesson Progress Report
            </StyledExportButton>
          )}
          {displayHSCompletionReportButton && (
            <StyledExportButton
              color="primary"
              data-testid="hs-report-export-button"
              onClick={() => getLessonProgressReport('HS')}
              role="button"
              size="sm"
            >
              Export HS Lesson Progress Report
            </StyledExportButton>
          )}
        </fieldset>
      </Col>
    </React.Fragment>
  )
}

export default UserProfile
